import { useEffect, useRef, useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { computeAvailableBalance } from "@/hooks/useEnvironmentWallets";

const DEBOUNCE_MS = 350;

/**
 * Subscribes to real-time wallet balance changes for a user.
 *
 * Stability guarantees:
 * - Only fetches once auth is fully ready (prevents 401/empty races during session restore).
 * - If a refresh arrives while another is in-flight, a *trailing* refresh is queued
 *   (no dropped events that would leave the UI stale).
 * - Never overwrites a previously populated wallet array with `[]` on transient errors —
 *   keeps the last-good snapshot visible until a successful payload arrives.
 * - Refetches on realtime resubscribe, window focus, visibilitychange, and `online`
 *   to recover from sleep / network changes / PWA resume.
 * - Also invalidates the canonical React Query cache (`environment_wallets`) so any
 *   consumer using `useEnvironmentWallets` stays in sync.
 *
 * @param tableName - "wallets" or "sandbox_wallets" depending on environment mode
 */
export function useRealtimeBalances(
  userId: string | undefined,
  setWallets: (wallets: any[]) => void,
  tableName: "wallets" | "sandbox_wallets" = "wallets"
) {
  const { loading: authLoading, session } = useAuth();
  const qc = useQueryClient();

  const setWalletsRef = useRef(setWallets);
  setWalletsRef.current = setWallets;

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlightRef = useRef(false);
  const pendingRef = useRef(false);
  const lastGoodRef = useRef<any[] | null>(null);

  const fetchWallets = useCallback(
    async (uid: string, table: string) => {
      if (inFlightRef.current) {
        // Queue a trailing refresh so we never drop a real change event.
        pendingRef.current = true;
        return;
      }
      inFlightRef.current = true;
      try {
        const { data, error } = await supabase
          .from(table as any)
          .select("*")
          .eq("user_id", uid);

        if (error) {
          console.warn("[useRealtimeBalances] fetch error:", error.message);
          return; // keep last-good snapshot
        }

        const rows = (data as any[]) || [];

        // Defensive: don't blow away a previously populated list with an empty array
        // unless the user genuinely has zero wallets (i.e., we never had any either).
        if (rows.length === 0 && lastGoodRef.current && lastGoodRef.current.length > 0) {
          return;
        }

        const enriched = rows.map((w) => ({
          ...w,
          held_balance: Number(w.held_balance || 0),
          frozen_balance: Number(w.frozen_balance || 0),
          available_balance: computeAvailableBalance(w),
        }));

        lastGoodRef.current = enriched;
        setWalletsRef.current(enriched);
        // Keep canonical react-query cache in sync for any other consumer.
        qc.invalidateQueries({ queryKey: ["environment_wallets", table, uid] });
      } finally {
        inFlightRef.current = false;
        if (pendingRef.current) {
          pendingRef.current = false;
          // Run the trailing refresh on the next tick to coalesce bursts.
          setTimeout(() => fetchWallets(uid, table), 0);
        }
      }
    },
    [qc]
  );

  const scheduleFetch = useCallback(
    (uid: string, table: string) => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => fetchWallets(uid, table), DEBOUNCE_MS);
    },
    [fetchWallets]
  );

  useEffect(() => {
    // Wait for auth to fully settle before subscribing/fetching to avoid
    // race conditions during session restore or token refresh.
    if (!userId || authLoading || !session) return;
    const table = tableName;

    // Initial sync
    fetchWallets(userId, table);

    const channel = supabase
      .channel(`${table}-realtime-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table, filter: `user_id=eq.${userId}` },
        () => scheduleFetch(userId, table)
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") fetchWallets(userId, table);
      });

    const withdrawalsChannel = supabase
      .channel(`withdrawal-balance-realtime-${userId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "withdrawal_requests", filter: `user_id=eq.${userId}` },
        () => scheduleFetch(userId, table)
      )
      .subscribe();

    const onFocus = () => fetchWallets(userId, table);
    const onOnline = () => fetchWallets(userId, table);
    const onVisibility = () => {
      if (document.visibilityState === "visible") fetchWallets(userId, table);
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      supabase.removeChannel(channel);
      supabase.removeChannel(withdrawalsChannel);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [userId, tableName, authLoading, session, fetchWallets, scheduleFetch]);
}
